import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { useQuery } from '@apollo/client';
import Loader from 'Components/Forms/Loader';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IPlotsFilters } from 'graphql/secure/queries/types/plot-query.types';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import { IGraphQlConnection } from 'types/core.types';
import { PLOTS_PER_PLOT_LIBRARY_PAGE } from 'utils/pagination.utils';
import { GET_PLOTS_WITH_FARMS } from 'graphql/secure/queries/plot.queries';
import { removeGraphConnections } from 'utils/graphql.utils';
import { useNavigate, useParams } from 'react-router-dom';
import PlotsTable from 'FeatureModules/Site/Components/PlotsTable/PlotsTable';
import { EPlotsTableColumnKeys } from 'FeatureModules/Site/Components/PlotsTable/plotsHeaderColumns';
import usePageTitle from 'hooks/usePageTitle';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import usePlotsMutations from 'FeatureModules/Site/hooks/usePlotMutations';
import ActionContainer from 'Components/ActionContainer';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const Plots: FC = () => {
  usePageTitle('Site plots');
  const { id } = useParams();
  const { openDialog } = useDialog();
  const { deletePlots } = usePlotsMutations();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const navigation = useNavigate();
  const { data, loading, error, fetchMore } = useQuery<
    { plots: IGraphQlConnection<IPlot> },
    { filters: IPlotsFilters; first: number }
  >(GET_PLOTS_WITH_FARMS, {
    skip: !id,
    variables: id
      ? {
          filters: { farmIds: [id] },
          first: PLOTS_PER_PLOT_LIBRARY_PAGE,
        }
      : undefined,
  });

  const hasNextPage: boolean = !!data?.plots?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.plots?.pageInfo?.endCursor;

  const plots = useMemo(
    () => (data?.plots ? removeGraphConnections(data.plots) : []),
    [data?.plots]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleClickOnPlot = (plot: IPlot) => {
    navigation(`${plot.id}`);
  };

  const handleEditPlot = (plot: IPlot) => {
    openDialog({
      type: 'ADD_EDIT_FARM_PLOT',
      props: {
        plot,
      },
    });
  };

  const handleAddPlot = () => {
    openDialog({
      type: 'ADD_EDIT_FARM_PLOT',
      props: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        farmId: id!,
      },
    });
  };

  const handleDeletePlot = async (plot: IPlot) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete plot',
        text: 'Are you sure you want to delete this plot? All data will be lost and you will not be able to recover the plot.',
        submitText: 'Delete',
        itemTitle: `${plot.externalId ? `${plot.externalId} | ` : ''} ${plot.title}`,
        displayCloseButton: true,
        onSubmit: () => {
          deletePlots({
            variables: { ids: [plot.id] },
            onCompleted: () => {
              setSuccessMessage('Plot successfully deleted');
            },
            onError: () => {
              setErrorMessage('Failed to delete plot');
            },
          });
        },
        onCancel: () => undefined,
      },
    });
  };

  if (error) return <ErrorState />;

  if (!plots.length && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Site plots"
          titleExtension={plots.length > 0 ? plots[0].farm.title : ''}
          goBackLabel="Site overview"
          goBackUrl={`/sites/${id}`}
        />
        <Box mt={4}>
          <EmptyStateCard
            data-testid="plots-getting-started-card"
            data-testid-button="plots-getting-started-card-button"
            title="Add your plots by mapping them on your farm site"
            variant={EItemTypes.LOCATION}
            background={EBackgroundTypes.WORLD}
            buttonText="Add plot"
            onCreateItemClick={handleAddPlot}
            size={EAvailableSizes.LARGE}
            renderIllustration={
              <CardIllustration
                image={EIllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Site plots"
        titleExtension={plots.length > 0 ? plots[0].farm.title : ''}
        goBackLabel="Site overview"
        goBackUrl={`/sites/${id}`}
      />
      <Box mb={1} />

      <ActionContainer>
        <ThemeButton
          color="PRIMARY"
          startIcon={<Icon name="plus" color="WHITE" />}
          onClick={handleAddPlot}
        >
          Add plot
        </ThemeButton>
      </ActionContainer>

      {loading && <Loader />}

      {plots.length > 0 && !loading && (
        <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
          <PlotsTable
            columns={[
              EPlotsTableColumnKeys.TITLE,
              EPlotsTableColumnKeys.CREATED_TIMESTAMP,
              EPlotsTableColumnKeys.ACTIONS,
            ]}
            plots={plots}
            onEdit={handleEditPlot}
            onClick={handleClickOnPlot}
            onDelete={handleDeletePlot}
          />
        </InfiniteScrollWrapper>
      )}
    </PageContainer>
  );
};

export default Plots;
